import { inject, Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { Firestore } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import { catchError, defer, delay, filter, from, interval, map, of, retryWhen, scan, Subscription, switchMap, take, tap, throwError } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { CommonService } from "./common/common.service";
@Injectable({
  providedIn: "root",
})
export class PaymentLogService {
  myuuid: string = uuidv4();
  tokenValidaterSub: Subscription;
  private commonService = inject(CommonService);
  constructor(
    private afireAuth: AngularFireAuth,
    protected firestore: Firestore,

  ) {
    localStorage.setItem("myuuid", this.myuuid);
  }

  updateLogToLocal(data) {
    const existingPaymentDetails = this.getPaymentLogInfoIfAny();
    localStorage.setItem("paymentLog", JSON.stringify({ ...existingPaymentDetails, ...data }));
    this.syncPaymentLogToFireStore();
  }

  private getPaymentLogInfoIfAny() {
    const paymentLogData = localStorage.getItem("paymentLog");
    if (paymentLogData) {
      return JSON.parse(paymentLogData);
    }
    return {};
  }

  syncPaymentLogToFireStore() {
    this.afireAuth.currentUser.then((user) => {
      if (!user) {
        let token = localStorage.getItem("fbToken");
        if (token) {
          this.manualLogin(token);
        } else {
          const interval = setInterval(() => {
            let token = localStorage.getItem("fbToken");
            if (token) {
              clearInterval(interval);
              this.manualLogin(token);
            }
          }, 1000);
        }
      } else {
        this.updatePaymentLogsToFirebase();
      }
    }).catch((error) => {
      return error;
    });
  }

  private manualLogin(token: string) {
    this.tokenValidaterSub = this.getV2CloudFunctionToken("tokenvalidater", { token }).subscribe({
      next: async (res) => {
        const { status, token, token_refresh } = res;
        if (status && token) {
          if (token_refresh) {
            localStorage.setItem("fbToken", token);
          }
          this.afireAuth.signInWithCustomToken(token).then(() => {
            this.afireAuth.currentUser.then((d) => {
              if (d) {
                this.updatePaymentLogsToFirebase();
              }
            }).catch((error) => {
              return error;
            });
          }).catch((error) => {
            return error;
          });
        }
      },
      error: (err) => {
        return err;
      }
    });
  }

  // This function is used to get the token from the cloud function
  public getV2CloudFunctionToken(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  private updatePaymentLogsToFirebase() {
    const paymentInfo = this.getPaymentLogInfoIfAny();
    if (!paymentInfo || !Object.keys(paymentInfo).length) return;

    const cleanedObject = this.cleanObject(paymentInfo);

    this.commonService.getUid("PaymentLogService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        // Poll every second until myuuid is available, then update once and stop.
        return interval(1000).pipe(
          map(() => localStorage.getItem("myuuid")), // still needed if dynamically generated
          filter((myuuid): myuuid is string => !!myuuid),
          take(1), // Clear polling by completing the observable once myuuid exists
          switchMap((myuuid) => {
            const path_id = "At4Vyy58QQ2vknYAhw7T";
            const doc_data = cleanedObject;
            const dv = { docId: myuuid };
            const method = "update";

            // IMPORTANT: return the observable so the outer switchMap never
            // receives `undefined` (which throws "You provided 'undefined'
            // where a stream was expected").
            return this.getV2CloudFunctionToken("updatefirestoredoc", {
              path_id,
              doc_data,
              dv,
              method,
            }).pipe(
              tap(() => console.log("Payment logs updated successfully")),
              catchError((err) => {
                console.error('Error updating payment logs:', err);
                return of(null);
              })
            );
          })
        );
      })
    ).subscribe();
  }


  private cleanObject(input) {
    if (typeof input === "object" && input !== null) {
      if (Array.isArray(input)) {
        return input.map(this.cleanObject).filter((item) => item !== null && item !== undefined);
      }
      return Object.fromEntries(
        Object.entries(input)
          .map(([key, val]) => [key, this !== undefined ? this?.cleanObject(val) : null])
          .filter(([k, v]) => v !== null && v !== undefined && k)
      );
    }
    return input;
  }

  public generateNewUUID(): void {
    this.myuuid = uuidv4();
    localStorage.setItem("myuuid", this.myuuid);
  }

  ngOnDestroy() {
    this.tokenValidaterSub?.unsubscribe();
  }

}
